import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  subscribeToAdminStatus,
  subscribeToUsers,
  subscribeToAvailableQuests,
  createQuestRequest,
  approveQuestRequest,
  rejectQuestRequest,
  subscribeToQuestRequests,
  getUserQuestRequests,
  searchUserByEmail,
  searchUserByUsername,
  searchUserByUID,
  fetchUsersPaginated,
  getUserCount
} from '../services/adminService';
import {
  addXPEntryToUser,
  deleteXPEntryFromUser,
  abandonActiveQuest
} from '../services/firestoreService';

export const useAdmin = () => {
  const { currentUser } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminLoading, setAdminLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [availableQuests, setAvailableQuests] = useState([]);
  const [questRequests, setQuestRequests] = useState([]);
  const [pendingQuestRequests, setPendingQuestRequests] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [questsLoading, setQuestsLoading] = useState(false);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Track subscriptions to avoid multiple subscriptions
  const [usersSubscription, setUsersSubscription] = useState(null);
  const [questsSubscription, setQuestsSubscription] = useState(null);
  const [requestsSubscription, setRequestsSubscription] = useState(null);

  // Check admin status when user changes
  useEffect(() => {
    if (!currentUser) {
      setIsAdmin(false);
      setAdminLoading(false);
      return;
    }

    setAdminLoading(true);
    
    // Subscribe to admin status changes
    const unsubscribe = subscribeToAdminStatus(currentUser.uid, (adminStatus) => {
      setIsAdmin(adminStatus);
      setAdminLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, [currentUser]);

  // Cleanup subscriptions when component unmounts or admin status changes
  useEffect(() => {
    if (!isAdmin) {
      // Clean up all subscriptions
      if (usersSubscription) {
        usersSubscription();
        setUsersSubscription(null);
      }
      if (questsSubscription) {
        questsSubscription();
        setQuestsSubscription(null);
      }
      if (requestsSubscription) {
        requestsSubscription();
        setRequestsSubscription(null);
      }
      
      // Clear data
      setUsers([]);
      setAvailableQuests([]);
      setQuestRequests([]);
      setPendingQuestRequests([]);
    }
  }, [isAdmin, usersSubscription, questsSubscription, requestsSubscription]);

  // Lazy loading functions
  const loadUsers = useCallback(() => {
    if (!isAdmin || usersSubscription) return;

    setUsersLoading(true);
    const unsubscribe = subscribeToUsers((usersData) => {
      setUsers(usersData);
      setUsersLoading(false);
    });

    setUsersSubscription(() => unsubscribe);
  }, [isAdmin, usersSubscription]);

  const loadQuests = useCallback(() => {
    if (!isAdmin || questsSubscription) return;

    setQuestsLoading(true);
    const unsubscribe = subscribeToAvailableQuests((questsData) => {
      setAvailableQuests(questsData);
      setQuestsLoading(false);
    });

    setQuestsSubscription(() => unsubscribe);
  }, [isAdmin, questsSubscription]);

  const loadQuestRequests = useCallback(() => {
    if (!isAdmin || requestsSubscription) return;

    setRequestsLoading(true);
    
    const unsubscribe = subscribeToQuestRequests((requestsData) => {
      try {
        if (Array.isArray(requestsData)) {
          setQuestRequests(requestsData);
          // Derive pending requests from all requests
          const pendingRequests = requestsData.filter(request => request.status === 'pending');
          setPendingQuestRequests(pendingRequests);
          setError(null);
        } else {
          console.error('Invalid quest requests data received:', requestsData);
          setError('Invalid quest requests data received');
        }
      } catch (err) {
        console.error('Error processing quest requests:', err);
        setError('Error processing quest requests');
      }
      setRequestsLoading(false);
    });

    setRequestsSubscription(() => unsubscribe);
  }, [isAdmin, requestsSubscription]);

  // Quest approval functions
  const handleCreateQuestRequest = useCallback(async (userId, questId, questData) => {
    try {
      setError(null);
      const requestId = await createQuestRequest(userId, questId, questData);
      return requestId;
    } catch (err) {
      console.error('Error creating quest request:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  const handleApproveQuestRequest = useCallback(async (requestId, message = '') => {
    try {
      setError(null);
      await approveQuestRequest(requestId, currentUser.uid, message);
    } catch (err) {
      console.error('Error approving quest request:', err);
      setError(err.message);
      throw err;
    }
  }, [currentUser]);

  const handleRejectQuestRequest = useCallback(async (requestId, message = '') => {
    try {
      setError(null);
      await rejectQuestRequest(requestId, currentUser.uid, message);
    } catch (err) {
      console.error('Error rejecting quest request:', err);
      setError(err.message);
      throw err;
    }
  }, [currentUser]);

  const getQuestRequestsForUser = useCallback(async (userId) => {
    try {
      setError(null);
      const requests = await getUserQuestRequests(userId);
      return requests;
    } catch (err) {
      console.error('Error getting user quest requests:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  const handleSearchUserByEmail = useCallback(async (email) => {
    try {
      setError(null);
      const user = await searchUserByEmail(email);
      return user;
    } catch (err) {
      console.error('Error searching user by email:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  const handleSearchUserByUsername = useCallback(async (username) => {
    try {
      setError(null);
      const user = await searchUserByUsername(username);
      return user;
    } catch (err) {
      console.error('Error searching user by username:', err);
      setError(err.message);
      throw err;
    }
  }, []);


  const handleSearchUserByUID = useCallback(async (uid) => {
    try {
      setError(null);
      const user = await searchUserByUID(uid);
      return user;
    } catch (err) {
      console.error('Error searching user by UID:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  // XP History management functions
  const handleAddXPEntry = useCallback(async (userId, xpEntryData) => {
    try {
      setError(null);
      await addXPEntryToUser(userId, xpEntryData);
    } catch (err) {
      console.error('Error adding XP entry:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  const handleDeleteXPEntry = useCallback(async (userId, entryId) => {
    try {
      setError(null);
      await deleteXPEntryFromUser(userId, entryId);
    } catch (err) {
      console.error('Error deleting XP entry:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  // Active quest management
  const handleDeleteActiveQuest = useCallback(async (userId) => {
    try {
      setError(null);
      await abandonActiveQuest(userId);
    } catch (err) {
      console.error('Error deleting active quest:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  // Paginated user fetching
  const handleFetchUsersPaginated = useCallback(async (pageSize, lastDoc) => {
    try {
      setError(null);
      return await fetchUsersPaginated(pageSize, lastDoc);
    } catch (err) {
      console.error('Error fetching paginated users:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  const handleGetUserCount = useCallback(async () => {
    try {
      setError(null);
      return await getUserCount();
    } catch (err) {
      console.error('Error getting user count:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  // Helper functions
  const getUserStats = useCallback((user) => {
    const xpHistory = user.xpHistory || {};
    const completedQuests = Object.values(xpHistory).filter(q => q.status === 'completed');
    const failedQuests = Object.values(xpHistory).filter(q => q.status === 'failed');
    const activeQuests = Object.values(xpHistory).filter(q => q.status === 'active');
    const totalXP = completedQuests.reduce((sum, quest) => sum + (quest.value || 0), 0);

    return {
      totalXP,
      completedCount: completedQuests.length,
      failedCount: failedQuests.length,
      activeCount: activeQuests.length,
      successRate: completedQuests.length + failedQuests.length > 0 
        ? Math.round((completedQuests.length / (completedQuests.length + failedQuests.length)) * 100)
        : 0
    };
  }, []);

  const getQuestStats = useCallback((quest) => {
    // This could be enhanced with actual usage stats from users
    return {
      timesStarted: 0, // Would need to aggregate from users
      timesCompleted: 0,
      averageCompletionRate: 0,
      isActive: quest.isActive !== false
    };
  }, []);

  return {
    // Admin status
    isAdmin,
    adminLoading,
    isAuthenticated: !!currentUser,
    
    // Data
    users,
    availableQuests,
    questRequests,
    pendingQuestRequests,
    usersLoading,
    questsLoading,
    requestsLoading,
    error,
    
    // Lazy loading functions
    loadUsers,
    loadQuests,
    loadQuestRequests,
    
    // Helper functions
    getUserStats,
    getQuestStats,
    
    // Quest approval management
    createQuestRequest: handleCreateQuestRequest,
    approveQuestRequest: handleApproveQuestRequest,
    rejectQuestRequest: handleRejectQuestRequest,
    getQuestRequestsForUser,
    
    // User management
    searchUserByEmail: handleSearchUserByEmail,
    searchUserByUsername: handleSearchUserByUsername,
    searchUserByUID: handleSearchUserByUID,
    
    // XP History management
    addXPEntry: handleAddXPEntry,
    deleteXPEntry: handleDeleteXPEntry,

    // Active quest management
    deleteActiveQuest: handleDeleteActiveQuest,

    // Paginated users
    fetchUsersPaginated: handleFetchUsersPaginated,
    getUserCount: handleGetUserCount,

    // Utility
    clearError: () => setError(null)
  };
}; 